import React, { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import { Button, DeleteOutlined, Popconfirm, Smile, Space } from "@ctra/components";
import { EnterpriseAppState, Events } from "@ctra/api";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { isPending } from "@ctra/utils";

import { testIds, useEvent } from "@events";
import { CreateAskIdaOrigin, CreateAskIdaTrackerDialogContext } from "@ask-ida";

import styles from "./GenericLabelEventCta.module.less";

/**
 * Custom event call to action component
 * @constructor
 */
export const GenericLabelEventCta: FC = () => {
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState<boolean>(false);
  const { t } = useTranslation();

  const {
    events: {
      list: {
        entry: {
          cta: {
            ask,
            remove: { confirm, yes, no }
          }
        }
      }
    }
  } = Content;

  const {
    event: {
      id,
      farmId,
      source: { type, id: sourceID },
      children: childEvents
    }
  } = useEvent();

  const dispatch = useDispatch();

  /**
   * Tell if the given event is being deleted
   */
  const isDeleting = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Events.types.DELETE_EVENT, { primaryValue: id })
  );

  return (
    <CreateAskIdaTrackerDialogContext.Provider>
      <Space data-testid={testIds.cta.wrapper}>
        {!childEvents.length && (
          <CreateAskIdaTrackerDialogContext.Consumer>
            {({ api: { open } }) => (
              <Button
                className={styles.AskIdaButton}
                data-testid={testIds.cta.button.askIda}
                onClick={() => {
                  open(id, CreateAskIdaOrigin.timeline, farmId);
                }}
                size="small"
                icon={<Smile />}
              >
                {t<string>(ask)}
              </Button>
            )}
          </CreateAskIdaTrackerDialogContext.Consumer>
        )}
        <Popconfirm
          visible={confirmDeleteVisible}
          placement="topRight"
          title={t<string>(confirm)}
          onConfirm={() => {
            /**
             * Delete the related events if we have any
             */
            if (childEvents.length) {
              _.forEach(childEvents, (childEvent) => {
                const {
                  id: relatedId,
                  source: { type: relatedSourceType, id: relatedSourceID }
                } = childEvent;

                if (relatedSourceID) {
                  dispatch(Events.actions.deleteEvent.start(relatedId, relatedSourceType, relatedSourceID));
                } else {
                  console.error(`Related source ID is missing for event ${relatedId}.`);
                }
              });
            }

            /**
             * Delete the main event aas well
             */
            dispatch(Events.actions.deleteEvent.start(id, type, sourceID));
          }}
          okText={t<string>(yes)}
          okButtonProps={{ loading: isDeleting }}
          onCancel={() => setConfirmDeleteVisible(false)}
          cancelText={t<string>(no)}
        >
          <Button
            data-testid={testIds.cta.button.delete}
            onClick={() => setConfirmDeleteVisible(true)}
            size="small"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      </Space>
    </CreateAskIdaTrackerDialogContext.Provider>
  );
};
